import { Link } from "@tanstack/react-router";
import { flowArrow, formatCurrency } from "@/utils/formatters";

export type ActivityFlow = "income" | "expense";

export interface ActivityItem {
  id: string;
  name: string;
  /** "Gasto · 14 sep". */
  meta: string;
  flow: ActivityFlow;
  /** Monto absoluto; el signo y la flecha los pone `flow`. */
  amount: number;
}

export interface ActivityGroup {
  /** "Antes", "Hoy"… */
  label: string;
  items: ActivityItem[];
}

export interface ActivityListProps {
  title: string;
  groups: ActivityGroup[];
  moreLabel: string;
  moreTo: string;
  /** Texto para lectores de pantalla antes del monto. */
  flowSpoken: Record<ActivityFlow, string>;
}

const FLOW_STYLE: Record<ActivityFlow, { sign: string; color: string }> = {
  income: { sign: "", color: "text-[var(--app-success)]" },
  expense: { sign: "-", color: "text-[var(--app-danger)]" },
};

/** Movimientos recientes agrupados por fecha, en tarjeta neutra. */
export function ActivityList({ title, groups, moreLabel, moreTo, flowSpoken }: ActivityListProps) {
  return (
    <section aria-label={title} className="rounded-[var(--ds-radius-lg)] bg-[var(--ds-surface-raised)] p-5 text-[var(--ds-ink)] sm:p-6">
      <div className="mb-3 text-sm leading-5 text-[var(--ds-ink-muted)]">{title}</div>
      {groups.map((group, gi) => (
        <div key={group.label}>
          <div className={`mb-2 text-[11px] font-medium uppercase leading-[14px] tracking-[0.06em] text-[var(--ds-ink-muted)] ${gi === 0 ? "mt-0" : "mt-4"}`}>
            {group.label}
          </div>
          {group.items.map((item) => {
            const style = FLOW_STYLE[item.flow];
            return (
              <div key={item.id} className="flex items-center justify-between gap-3 py-2 text-base leading-6 sm:-mx-2 sm:rounded-[var(--ds-radius-sm)] sm:p-2 sm:hover:bg-[var(--ds-line)]">
                <div className="min-w-0">
                  <span className="block truncate">{item.name}</span>
                  <span className="block text-sm leading-5 text-[var(--ds-ink-muted)]">{item.meta}</span>
                </div>
                <span className={`flex-none whitespace-nowrap font-medium ${style.color}`}>
                  <span className="sr-only">{flowSpoken[item.flow]}: </span>
                  <span aria-hidden="true">{flowArrow(item.flow)} </span>
                  {`${style.sign}${formatCurrency(Math.abs(item.amount))}`}
                </span>
              </div>
            );
          })}
        </div>
      ))}
      <Link
        to={moreTo}
        className="mt-4 inline-flex h-9 items-center rounded-full border-[1.5px] border-solid border-[var(--ds-ink)] px-4 text-sm leading-none text-[var(--ds-ink)] no-underline"
      >
        {moreLabel} ›
      </Link>
    </section>
  );
}
